import React, { useState } from 'react';

/**
 * Signup Form Component
 * Handles account creation with Supabase
 */
export default function SignupForm() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pronouns, setPronouns] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Check if user is already logged in
  React.useEffect(() => {
    const checkLogin = async () => {
      const { isLoggedIn } = await import('../auth');
      if (isLoggedIn()) {
        const urlParams = new URLSearchParams(window.location.search);
        const redirectUrl = urlParams.get('redirect');

        // Security check: ensure redirectUrl is a relative path starting with '/'
        // but NOT starting with '//' to prevent protocol-relative open redirects
        const isSafeRedirect = redirectUrl && redirectUrl.startsWith('/') && !redirectUrl.startsWith('//');

        if (isSafeRedirect) {
          window.location.href = redirectUrl;
        } else if (window.location.pathname.startsWith('/subscribe') && window.location.pathname !== '/subscribe/select-tier/') {
          // Default for the standard subscribe flow
          window.location.href = '/subscribe/select-tier/';
        }
        // If we're on /fundraiser/ (or another embedded context) and already logged in,
        // we do nothing here. The parent page logic (in index.njk) handles hiding
        // the form and showing the tier selection when it detects a session.
      }
    };
    checkLogin();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate passwords match
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setIsLoading(true);

    try {
      // Get Supabase client from window (set by auth.js)
      const supabase = window.supabaseClient;
      if (!supabase) {
        throw new Error('Supabase client not initialized');
      }

      // Create account with Supabase
      const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            name,
            pronouns
          }
        }
      });

      if (signUpError) throw signUpError;

      if (signUpData.user) {
        // Immediately log in with the new credentials
        const { data: loginData, error: loginError } = await supabase.auth.signInWithPassword({
          email,
          password
        });

        if (loginError) throw loginError;

        // Ensure we have a valid session before proceeding
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        if (sessionError || !session) {
          throw new Error('Failed to establish session after login');
        }

        // Call create-profile edge function
        try {
          const { data: profileData, error: profileError } = await supabase.functions.invoke('create-profile', {
            body: {
              name,
              email,
              pronouns: pronouns || null
            },
            headers: {
              Authorization: `Bearer ${session.access_token}`
            }
          });

          if (profileError) {
            console.error('Profile creation error:', profileError);
            // Don't throw error - continue with signup flow even if profile creation fails
          } else {
            console.log('Profile created successfully:', profileData);
          }
        } catch (profileErr) {
          console.error('Profile creation failed:', profileErr);
          // Don't throw error - continue with signup flow even if profile creation fails
        }

        // Use the auth system's handleLogin function to properly sync state
        const authState = {
          userId: session.user.id,
          email: session.user.email,
          name,
          pronouns: pronouns || '',
          loggedInAt: new Date().toISOString()
        };

        localStorage.setItem('pinkWalletAuth', JSON.stringify(authState));

        // Dispatch custom event to sync with auth system
        window.dispatchEvent(new CustomEvent('authStateChanged', { detail: authState }));

        // Store data in sessionStorage for tier selection
        sessionStorage.setItem('pinkWalletAccount', JSON.stringify({
          name,
          email,
          pronouns,
          userId: loginData.user.id
        }));

        // Navigate to custom redirect, tier selection, or reload
        const urlParams = new URLSearchParams(window.location.search);
        const redirectUrl = urlParams.get('redirect');

        // Security check: ensure redirectUrl is a relative path
        const isSafeRedirect = redirectUrl && redirectUrl.startsWith('/') && !redirectUrl.startsWith('//');

        if (isSafeRedirect) {
          window.location.href = redirectUrl;
        } else if (window.location.pathname.startsWith('/subscribe') && window.location.pathname !== '/subscribe/select-tier/') {
          window.location.href = '/subscribe/select-tier/';
        } else {
          // For embedded contexts like /fundraiser/, just reload so the parent 
          // page detects the new session and updates the UI accordingly.
          window.location.reload();
        }

      }

    } catch (err) {
      console.error('Sign up error:', err);
      setError(err.message || 'Failed to create account. Please try again.');
      setIsLoading(false);
    }
  };

  return (
    <section className="py-12 md:py-16">
      <div className="container-custom max-w-2xl">
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold mb-4">Create Your Account</h1>
          <p className="text-lg text-gray-600">Let's get started with your Pink Wallet membership</p>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6 md:p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                Full Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                placeholder="Enter your full name"
                disabled={isLoading}
              />
            </div>

            <div>
              <label htmlFor="pronouns" className="block text-sm font-medium text-gray-700 mb-2">
                Pronouns <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="pronouns"
                name="pronouns"
                required
                value={pronouns}
                onChange={(e) => setPronouns(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                placeholder="e.g., she/her, he/him, they/them"
                disabled={isLoading}
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email Address <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                placeholder="your.email@example.com"
                disabled={isLoading}
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Password <span className="text-red-500">*</span>
              </label>
              <input
                type="password"
                id="password"
                name="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                placeholder="Create a password"
                disabled={isLoading}
              />
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                Confirm Password <span className="text-red-500">*</span>
              </label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                placeholder="Re-enter your password"
                disabled={isLoading}
              />
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                {error}
              </div>
            )}

            <div className="pt-4">
              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-brand-600 text-white font-semibold py-3 px-6 rounded-lg hover:bg-brand-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Creating Account...' : 'Continue'}
              </button>
            </div>

            <div className="text-center text-sm text-gray-600">
              Already have an account?{' '}
              <a href={`/login/?redirect=${new URLSearchParams(window.location.search).get('redirect') || window.location.pathname}`} className="text-brand-700 hover:text-brand-800 font-medium">
                Log in
              </a>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}
